"use client";

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getDatabase, ref as dbRef, onValue } from "firebase/database";
import { db } from "../Firebase/firebase-config";
import { FaInfoCircle } from "react-icons/fa";
import "./NewOrder.css";

const NewOrder = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const auth = getAuth();
  const database = getDatabase();

  useEffect(() => {
    const connectedRef = dbRef(database, ".info/connected");

    // Check network first
    if (!navigator.onLine) {
      setError("You are offline. Please check your internet connection.");
      setLoading(false);
      return;
    }

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setError("Session expired. Please login again.");
        setLoading(false);
        return;
      }
      setError("");

      const userId = user.uid;
      const defaultProductsRef = dbRef(database, "ProductsCatalog");
      const userProductsRef = dbRef(database, `ProductsCatalog/${userId}`);

      // Check Firebase connection state
      onValue(connectedRef, (snap) => {
        if (snap.val() === false) {
          setError("Session expired. Please login again.");
          setLoading(false);
          return;
        }
        setError("");
        // Fetch data
        const fetchData = async () => {
          try {
            const defaultSnapshot = await new Promise((resolve) =>
              onValue(defaultProductsRef, (snap) => resolve(snap), { onlyOnce: true })
            );
            const userSnapshot = await new Promise((resolve) =>
              onValue(userProductsRef, (snap) => resolve(snap), { onlyOnce: true })
            );

            let allProducts = [];

            if (defaultSnapshot.exists()) {
              const data = defaultSnapshot.val();
              const defaults = Object.entries(data)
                .filter(([_, v]) => v.name && !v.userId)
                .map(([k, v]) => ({ id: k, ...v }));
              allProducts = [...allProducts, ...defaults];
            }

            if (userSnapshot.exists()) {
              const data = userSnapshot.val();
              const customs = Object.entries(data)
                .filter(([_, v]) => v.name)
                .map(([k, v]) => ({ id: k, ...v }));
              allProducts = [...allProducts, ...customs];
            }

            setProducts(allProducts);
            setLoading(false);
          } catch (err) {
            console.error("Product fetch error:", err);
            setError("Error fetching products. Please try again later.");
            setLoading(false);
          }
        };

        fetchData();
      });
    });

    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    const filtered = products.filter((product) => {
      const name = product.name?.toLowerCase() || "";
      const category = product.category?.toLowerCase() || "";
      return (
        (selectedCategory === "all" || category === selectedCategory) &&
        name.includes(searchTerm.toLowerCase())
      );
    });
    setFilteredProducts(filtered);
  }, [products, selectedCategory, searchTerm]);

  const categories = [
    { id: "all", name: "All Products", count: products.length },
    ...Array.from(new Set(products.map((p) => p.category).filter(Boolean))).map((category) => ({
      id: category.toLowerCase(),
      name: category.charAt(0).toUpperCase() + category.slice(1),
      count: products.filter((p) => p.category?.toLowerCase() === category.toLowerCase()).length,
    })),
  ];

  const handleOrderNow = (product) => {
    const name = product.name?.toLowerCase() || "";
    const category = product.category?.toLowerCase() || "";

    if (name.includes("bulker cement") || name.includes("cement bulker")) {
      navigate("/dashboard/product-order-form", { state: { product } });
    } else if (name.includes("cement") || category.includes("cement")) {
      navigate("/dashboard/product-order-form", { state: { product } });
    } else if (name.includes("steel") || category.includes("steel")) {
      navigate("/dashboard/steel-order-form", { state: { product } });
    } else if (name.includes("machinery") || category.includes("machinery")) {
      navigate("/dashboard/machinery-order-form", { state: { product } });
    } else {
      navigate("/dashboard/product-order-form", { state: { product } });
    }
  };

  const handleImageClick = (product) => setSelectedImage(product);
  const closeImageModal = () => setSelectedImage(null);
  const neworderAlarm = () => {
    alert(
      "Welcome to the Product Catalog!\n\n" +
      "- Browse available products\n" +
      "- Filter by categories\n" +
      "- Click images for a larger view\n" +
      "- Place orders directly"
    );
  };

  // --- Rendering Logic with Error + Loading State ---
  if (loading) {
    return <div className="new-loading-container">
      <div className="new-loader"></div>
    </div>;
  }

  if (error) {
    return (
      <div className="error">
        {error}
      </div>
    );
  }

  return (
    <div className="container-fluid new-order-container">
      <div className="header">
        <h1 className="page-title text-center">
          Product Catalog{" "}
          <FaInfoCircle
            style={{ cursor: "pointer", fontSize: "1.2rem", color: "#EA8716" }}
            onClick={neworderAlarm}
          />
        </h1>
      </div>
      <div className="content">
        <div className="sidebar">
          <div className="sidebar-header"><h2>Categories</h2></div>
          <div className="category-list">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`category-button fw-bold ${selectedCategory === category.id ? "active" : ""}`}
              >
                <span className="category-name">
                  {selectedCategory === category.id && <span className="category-indicator">›</span>}
                  {category.name}
                </span>
                <span className="category-count">{category.count}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="product-grid-container">
          {filteredProducts.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🛒</div>
              <h3 className="empty-title">No products found</h3>
              <p>Try changing your filter or search</p>
            </div>
          ) : (
            <div className="product-grid">
              {filteredProducts.map((product) => (
                <div key={product.id} className="Mypcontainer">
                  <div className="product-image-container">
                    <img
                      src={product.img || "/placeholder.svg"}
                      alt={product.name}
                      className="product-image"
                      onClick={() => handleImageClick(product)}
                    />
                  </div>
                  <div className="product-details">
                    <div className="product-header">
                      <h3 className="product-name">{product.name}</h3>
                      {product.category && (
                        <span className="product-category-badge">{product.category}</span>
                      )}
                    </div>
                    <p className="product-description">
                      {product.description || "High-quality construction product"}
                    </p>
                    <button onClick={() => handleOrderNow(product)} className="product-button">
                      Order Now
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {selectedImage && (
        <div className="image-modal" onClick={closeImageModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <span className="close-modal" onClick={closeImageModal}>
              &times;
            </span>
            <img
              src={selectedImage.img || "/placeholder.svg"}
              alt={selectedImage.name}
              className="modal-image"
            />
            <h3 className="modal-title">{selectedImage.name}</h3>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewOrder;
